/**
 * workflowDraft — the SHARED WORKING DRAFT model behind the Workflows editor
 * (ADR-079 "AI-assisted, multi-mode workflow authoring studio" D1, Phase 5b
 * foundation; documentation/PRD-workflow-editor.md §6/§7).
 *
 * ADR-079 D1: "An editor session holds a single working draft of the
 * activity's DSL … Every mode reads and writes this one draft; the live
 * graph preview and live validation reflect it continuously." This module is
 * that draft's read/write surface for the two modes active in 5b:
 *
 *   - YAML mode edits the raw text directly; {@link parseDraftYAML} turns it
 *     into the parsed object the live graph preview (workflowGraph.ts) and
 *     the Field-mode inspector both read.
 *   - Field mode edits the parsed object's SCALAR fields (agent.run,
 *     command.command/args, target, timeout_s, switch case exprs, for
 *     each/in/max) and add/remove/reorder of TOP-LEVEL `do` tasks, then
 *     {@link serializeDraftYAML} writes it back to YAML text — the single
 *     source of truth the propose pipeline commits.
 *
 * Deliberately has ZERO knowledge of React — like workflowGraph.ts, it is a
 * pure, DOM-free module so the trickiest part (serialization round-tripping)
 * is unit-testable without mounting anything. All mutators return a NEW
 * activity object (deep-cloned) rather than mutating their argument, so
 * React state updates stay simple `setDraftActivity(next)` calls.
 *
 * Deep structural nesting — adding a new switch branch, a new `for` body
 * task, or a new fork branch — is intentionally NOT exposed here (PRD-workflow-editor.md
 * §"Field mode" scope note): callers should direct the operator to YAML mode
 * for that. Editing the SCALAR fields of an already-existing nested task
 * (e.g. an agent step inside a `for` body) IS supported — {@link findTaskRaw}
 * walks the same for/fork/switch containers workflowGraph.ts's visitNode does.
 */
import * as yaml from 'js-yaml'
import { DSL_MARKER, type ParsedEntry, type TaskType, isPlainObject, parseEntry } from './workflowGraph'
import type { DataReferenceMatchCandidate, DataReferenceMatchLocation } from '../api/client'
import { insertAtCaret } from './insertAtCaret'

// ---------------------------------------------------------------------------
// Naming
// ---------------------------------------------------------------------------

/**
 * Normalize whatever an operator types for a new workflow into the kebab-case
 * activity name the API requires (`^[a-z][a-z0-9-]*$`). Satisfying the format
 * is the application's job, not the user's — so a natural label like
 * "Handle Refund" or a camelCase "readThreadFlow" just works. Lowercases,
 * splits camelCase word boundaries, turns any run of separators/symbols into a
 * single hyphen, drops leading non-letters (the name must start with a-z), and
 * trims/collapses hyphens. Returns "" when the input has no usable characters,
 * which the form treats as not-yet-valid.
 */
export function slugifyWorkflowName(raw: string): string {
  return raw
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2') // camelCase -> camel-Case
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') // any run of separators/symbols -> one hyphen
    .replace(/^[^a-z]+/, '') // must start with a letter
    .replace(/-+/g, '-') // collapse repeats
    .replace(/^-+|-+$/g, '') // trim ends
}

// ---------------------------------------------------------------------------
// Parse / serialize
// ---------------------------------------------------------------------------

export interface DraftParseResult {
  /** The parsed activity, or null if the YAML is empty/malformed/non-mapping. */
  activity: Record<string, unknown> | null
  /** A human-readable parse error, or null on success. */
  error: string | null
}

/** Parse a draft's YAML text into the activity object the graph preview and
 * Field mode both read. Never throws — parse failures come back as
 * `{activity: null, error}` so the caller can show them inline rather than
 * crash the editor. */
export function parseDraftYAML(text: string): DraftParseResult {
  let parsed: unknown
  try {
    parsed = yaml.load(text)
  } catch (e) {
    return { activity: null, error: e instanceof Error ? e.message : String(e) }
  }
  if (parsed === undefined || parsed === null) {
    return { activity: null, error: 'empty document' }
  }
  if (!isPlainObject(parsed)) {
    return { activity: null, error: 'the top level of an activity must be a YAML mapping' }
  }
  return { activity: parsed, error: null }
}

/** Serialize the activity object back to YAML text — what Field mode writes
 * into the draft and what the propose pipeline ultimately commits. */
export function serializeDraftYAML(activity: Record<string, unknown>): string {
  return yaml.dump(activity, { lineWidth: -1, noRefs: true })
}

// ---------------------------------------------------------------------------
// Deep clone (mutators never touch their argument)
// ---------------------------------------------------------------------------

function deepClone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T
}

// ---------------------------------------------------------------------------
// Locating a task anywhere in the tree (top-level `do`, a `for` body, a
// `fork`'s branches, or a `switch` clause's inline branch) — mirrors
// workflowGraph.ts's visitNode/visitList walk exactly, so "what counts as a
// nested container" never drifts between the read-side graph and the
// write-side editor.
// ---------------------------------------------------------------------------

interface FoundTask {
  ttype: string
  /** The task's raw spec — object for agent/command/set/run/for/fork,
   * array-of-clauses for switch. Same object reference as inside the tree
   * passed in, so mutating it in place mutates that tree. */
  raw: unknown
}

function findTaskRaw(items: unknown, taskName: string): FoundTask | null {
  if (!Array.isArray(items)) return null
  for (const item of items) {
    const parsed = parseEntry(item)
    if (!parsed) continue
    if (parsed.name === taskName) {
      return { ttype: parsed.ttype, raw: parsed.spec }
    }
    if (parsed.ttype === 'for' && isPlainObject(parsed.spec)) {
      const nested = findTaskRaw(parsed.spec.do, taskName)
      if (nested) return nested
    }
    if (parsed.ttype === 'fork' && isPlainObject(parsed.spec)) {
      const branches = parsed.spec.branches
      if (Array.isArray(branches)) {
        const nested = findTaskRaw(branches, taskName)
        if (nested) return nested
      }
    }
    if (parsed.ttype === 'switch' && Array.isArray(parsed.spec)) {
      for (const clause of parsed.spec) {
        if (!isPlainObject(clause)) continue
        const branch = 'case' in clause ? clause.then : clause.default
        if (Array.isArray(branch)) {
          const nested = findTaskRaw(branch, taskName)
          if (nested) return nested
        }
      }
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// Field-mode scalar edits
// ---------------------------------------------------------------------------

/** The scalar fields Field mode can write, across the task types that carry
 * them (PRD-workflow-editor.md §"Field mode"): agent.run, command.command,
 * command.args, target (agent|command), timeout_s (agent|command), and
 * for's each/in/max. `undefined` fields are left untouched; `null` or `''`
 * clears an optional field. */
export interface TaskScalarPatch {
  run?: string
  target?: string | null
  timeout_s?: number | null
  command?: string
  args?: string[]
  each?: string
  in?: string
  max?: number
  /** `python.code` — the inline body. Exclusive with `file`, which this form does not
   *  edit: a path is not a value, and swapping one for the other is a change of kind. */
  code?: string
  /** `run.workflow` — the sub-workflow's NAME (ADR-126 D11's literal-only field). */
  workflow?: string
  /** `approval.title` / `approval.preview` (ADR-203 D1). */
  title?: string
  preview?: string
}

/** Apply a scalar-field patch to the named task, wherever it lives in the
 * tree. Returns a new activity object; a no-op deep-clone if the task isn't
 * found or isn't an object-shaped spec (e.g. a `switch`, whose scalar edits
 * go through {@link updateSwitchCaseExpr} instead). */
export function updateTaskFields(
  activity: Record<string, unknown>,
  taskName: string,
  patch: TaskScalarPatch,
): Record<string, unknown> {
  const clone = deepClone(activity)
  const doList = isPlainObject(clone) ? clone.do : undefined
  const found = findTaskRaw(doList, taskName)
  if (!found || !isPlainObject(found.raw)) return clone

  const raw = found.raw
  const setOrDelete = (key: string, value: unknown): void => {
    if (value === undefined) return
    if (value === null || value === '') delete raw[key]
    else raw[key] = value
  }
  setOrDelete('run', patch.run)
  setOrDelete('target', patch.target)
  setOrDelete('timeout_s', patch.timeout_s)
  setOrDelete('command', patch.command)
  setOrDelete('args', patch.args)
  setOrDelete('each', patch.each)
  setOrDelete('in', patch.in)
  setOrDelete('max', patch.max)
  setOrDelete('code', patch.code)
  setOrDelete('workflow', patch.workflow)
  setOrDelete('title', patch.title)
  setOrDelete('preview', patch.preview)
  return clone
}

// ---------------------------------------------------------------------------
// MAPPING SITES — `set`, `python.inputs`, `run.inputs` (ADR-128's structural gap)
//
// Three task types hold a name -> value MAPPING rather than a scalar, and Field mode
// could not edit any of them: `TaskFieldForm` had no arm, so all three fell through to
// "use YAML mode". That also stranded their data-reference pickers — `validate_data_refs`
// has offered for `set`, `python.inputs` and `run.inputs` since the slot work, and no
// editor existed to open one from, so the offers were unreachable in the UI.
//
// WHY ONE SET OF FUNCTIONS FOR THREE SITES
// ---------------------------------------
// They differ in exactly one thing — WHERE the mapping lives (a `set`'s spec IS the
// mapping; `python`/`run` nest theirs under `inputs`) — and in nothing else. Three
// mutators would be three places for the same rule about blank keys and duplicates to
// drift apart, which is the ADR-129 D1 defect this programme keeps closing.
//
// THE SYNTAX DIFFERENCE IS NOT ENCODED HERE, DELIBERATELY
// ------------------------------------------------------
// `set` is an expression slot (`.data.a.b`) and the two `inputs` are template slots
// (`{{data.a.b}}`). That fact lives in `validate_data_refs.EXPRESSION_SLOTS` and reaches
// the UI through the picker's own offers, which return the token already spelled for the
// slot. Restating it here would be a second copy free to disagree with the first.
// ---------------------------------------------------------------------------

/** The three DSL sites that hold a name -> value mapping. These strings are also the
 *  PICKER SLOT names, which is why they are spelled with the dot: passing anything else
 *  to the affordance would serve offers shaped for a site the value is not going into. */
export type MappingSite = 'set' | 'python.inputs' | 'run.inputs'

/** The mapping site a task type owns, or null for the types that hold no mapping. */
export function mappingSiteFor(ttype: string): MappingSite | null {
  if (ttype === 'set') return 'set'
  if (ttype === 'python') return 'python.inputs'
  if (ttype === 'run') return 'run.inputs'
  return null
}

/** One row of a mapping editor. Ordered, because a YAML mapping is ordered and rewriting
 *  it in a different order would show up as a diff nobody asked for. */
export interface MappingEntry {
  key: string
  value: string
}

/** The task's mapping as ordered entries. Empty for a task that holds none, or whose
 *  mapping is malformed in the file — this reads a draft, so it must not throw on
 *  something the validator will reject anyway.
 *
 *  A non-string value is rendered through JSON so the operator SEES it rather than
 *  finding a blank box: `run.inputs` may legally hold a number or a list (only string
 *  values are scanned for references), and a blank box would invite overwriting a value
 *  that was fine. */
export function getTaskMapping(task: ParsedEntry): MappingEntry[] {
  const site = mappingSiteFor(task.ttype)
  if (!site) return []
  const spec = task.spec
  const mapping = site === 'set'
    ? spec
    : (isPlainObject(spec) ? spec.inputs : undefined)
  if (!isPlainObject(mapping)) return []
  return Object.entries(mapping).map(([key, value]) => ({
    key,
    value: typeof value === 'string' ? value : JSON.stringify(value) ?? '',
  }))
}

/** Why a set of entries cannot be written, or null when it can.
 *
 *  Both refusals are cases where writing would LOSE something silently:
 *
 *   - a DUPLICATE key cannot exist in a YAML mapping, so writing two rows with the same
 *     name keeps one and discards the other with no indication which;
 *   - an EMPTY `set` is rejected by the canonical validator ("set must be a non-empty
 *     mapping of name -> expression"), so writing it produces a file that fails CI from
 *     a click.
 *
 *  Returned as a string for the editor to show, rather than thrown: the operator is
 *  mid-edit and the state is legitimate to pass through on the way to a valid one.
 */
export function mappingRefusal(site: MappingSite, entries: MappingEntry[]): string | null {
  const named = entries.filter(e => e.key.trim() !== '')
  const seen = new Set<string>()
  for (const e of named) {
    const key = e.key.trim()
    if (seen.has(key)) {
      return `two rows are both named \`${key}\`. A mapping cannot hold the same name twice, so ` +
        `one would be dropped — and nothing would say which. Rename or remove one.`
    }
    seen.add(key)
  }
  if (site === 'set' && named.length === 0) {
    return 'a `set` task must bind at least one variable — the validator rejects an empty ' +
      'mapping, so this is not saved until a name is filled in.'
  }
  return null
}

/**
 * Write the named task's mapping. Returns a new activity object; a no-op deep-clone if the
 * task is not found, holds no mapping site, or the entries cannot be written.
 *
 * Rows with a BLANK KEY are skipped rather than written under an empty name: a row an
 * operator has added and not yet named is not a binding, and `'': value` is a binding
 * nothing can reference.
 *
 * The site is derived from the task's own `ttype` in the file rather than passed in, so a
 * caller cannot ask for a `run.inputs` write against a `set` task.
 */
export function updateTaskMapping(
  activity: Record<string, unknown>,
  taskName: string,
  entries: MappingEntry[],
): Record<string, unknown> {
  const clone = deepClone(activity)
  const doList = isPlainObject(clone) ? clone.do : undefined
  const found = findTaskRaw(doList, taskName)
  if (!found) return clone
  const site = mappingSiteFor(found.ttype)
  if (!site) return clone
  if (mappingRefusal(site, entries)) return clone

  const next: Record<string, unknown> = {}
  for (const entry of entries) {
    const key = entry.key.trim()
    if (key === '') continue
    next[key] = entry.value
  }

  if (site === 'set') {
    // A `set`'s spec IS the mapping, so the object is rewritten in place: replacing
    // `found.raw` would only rebind a local, since findTaskRaw hands back the object it
    // found inside the clone rather than a path to it.
    if (!isPlainObject(found.raw)) return clone
    for (const key of Object.keys(found.raw)) delete found.raw[key]
    Object.assign(found.raw, next)
    return clone
  }

  if (!isPlainObject(found.raw)) return clone
  // Absent rather than `inputs: {}` when there is nothing to pass — the same
  // delete-on-empty convention updateTaskFields uses, and it keeps a task an operator
  // emptied out looking like one that never had inputs.
  if (Object.keys(next).length === 0) delete found.raw.inputs
  else found.raw.inputs = next
  return clone
}

/** Set a `switch` task's clause `case` expression by clause index (the
 * `default` clause has no `case` to edit). Returns a new activity object; a
 * no-op deep-clone if the task/clause isn't found or isn't a `case` clause. */
export function updateSwitchCaseExpr(
  activity: Record<string, unknown>,
  taskName: string,
  clauseIndex: number,
  expr: string,
): Record<string, unknown> {
  const clone = deepClone(activity)
  const doList = isPlainObject(clone) ? clone.do : undefined
  const found = findTaskRaw(doList, taskName)
  if (!found || found.ttype !== 'switch' || !Array.isArray(found.raw)) return clone
  const clause = found.raw[clauseIndex]
  if (isPlainObject(clause) && 'case' in clause) clause.case = expr
  return clone
}

// ---------------------------------------------------------------------------
// Top-level `do` list: add / remove / reorder (Field mode's palette)
// ---------------------------------------------------------------------------

/** The top-level `do` list's task entries, parsed. Empty if `do` is missing
 * or not a list. */
export function getTopLevelTasks(activity: Record<string, unknown>): ParsedEntry[] {
  const doList = isPlainObject(activity) ? activity.do : undefined
  if (!Array.isArray(doList)) return []
  const out: ParsedEntry[] = []
  for (const item of doList) {
    const parsed = parseEntry(item)
    if (parsed) out.push(parsed)
  }
  return out
}

/** A minimal, schema-shaped default spec for a brand-new task of the given
 * type — what the Field-mode "add task" palette inserts before the operator
 * fills it in. */
export function defaultSpecFor(ttype: TaskType): unknown {
  switch (ttype) {
    case 'agent':
      return { run: '' }
    case 'command':
      return { command: '', args: [] }
    case 'switch':
      return [{ case: '', then: [] }, { default: [] }]
    case 'fork':
      return { branches: [] }
    case 'for':
      return { each: 'item', in: '', do: [], max: 10 }
    case 'run':
      return { workflow: '' }
    case 'set':
      return {}
    case 'approval':
      return { preview: '' }
    case 'python':
      // ADR-080's task type. Its absence here was a regression shipped with
      // ADR-128 step 1: `python` was added to ADDABLE_TASK_TYPES while this
      // switch still returned `undefined` for it, so adding one inserted
      // `{name: {python: undefined}}` — a file the validator rejects, produced
      // by a click. The `never` guard below is what stops the next widening
      // doing the same silently.
      return { code: '', inputs: {} }
    case 'unknown':
      // Not authorable — a rendering state meaning "this editor does not model
      // what is in the file". ADDABLE_TASK_TYPES excludes it; this arm exists so
      // the exhaustiveness check below holds over the whole union.
      return {}
    default: {
      const exhaustive: never = ttype
      throw new Error(`defaultSpecFor: no default spec for task type ${String(exhaustive)} — `
        + `add one here in the SAME change that widens TaskType, or the palette inserts undefined`)
    }
  }
}

/**
 * Where to insert a task, for the container-aware insert below.
 *
 * ADR-128 step 2. Until now Field mode could only append at the top level, and
 * said so: *"Adding switch branches or `for` bodies isn't supported here — use
 * YAML mode for deep structural changes."* That is the gap ADR-128 names — you
 * cannot place a node inside a `for` body from the graph — and it is what gates
 * the pickers (B10), because `for.in` + `command` only compose once a node can
 * live inside a loop.
 */
export interface ContainerTarget {
  /** The container task's name. */
  task: string
  /** `for` bodies and `fork` branches have one slot; a `switch` has one per clause. */
  clauseIndex?: number
}

/** The container kinds a task may be inserted INTO, and where their child list lives. */
function childListOf(ttype: string, spec: unknown, clauseIndex?: number): unknown[] | null {
  if (ttype === 'for') {
    if (!isPlainObject(spec)) return null
    if (!Array.isArray(spec.do)) spec.do = []
    return spec.do as unknown[]
  }
  if (ttype === 'fork') {
    if (!isPlainObject(spec)) return null
    if (!Array.isArray(spec.branches)) spec.branches = []
    return spec.branches as unknown[]
  }
  if (ttype === 'switch') {
    if (!Array.isArray(spec) || clauseIndex === undefined) return null
    const clause = spec[clauseIndex]
    if (!isPlainObject(clause)) return null
    // A case clause nests under `then`; the default clause under `default`.
    const key = 'case' in clause ? 'then' : 'default'
    if (!Array.isArray(clause[key])) clause[key] = []
    return clause[key] as unknown[]
  }
  return null
}

/** Is this task type a container a task can be placed inside? */
export function isContainerType(ttype: string): boolean {
  return ttype === 'for' || ttype === 'fork' || ttype === 'switch'
}

/**
 * Insert `{name: {ttype: spec}}` INSIDE a container task, at any depth.
 *
 * Returns the activity unchanged if the target is not found or is not a
 * container — a no-op rather than a throw, matching removeTopLevelTask's
 * posture, because the caller is a UI and a stale selection must not explode.
 */
export function addTaskInContainer(
  activity: Record<string, unknown>,
  target: ContainerTarget,
  name: string,
  ttype: TaskType,
  spec: unknown,
  atIndex?: number,
): Record<string, unknown> {
  const clone = deepClone(activity)
  const entry = { [name]: { [ttype]: spec } }

  // Depth-first over the whole tree: a container may itself be nested, which is
  // the "true nesting" half of step 2 — one level would just move the wall.
  const visit = (list: unknown): boolean => {
    if (!Array.isArray(list)) return false
    for (const item of list) {
      const parsed = parseEntry(item)
      if (!parsed) continue
      if (parsed.name === target.task && isContainerType(parsed.ttype)) {
        const children = childListOf(parsed.ttype, parsed.spec, target.clauseIndex)
        if (children === null) return false
        const at = atIndex === undefined
          ? children.length
          : Math.max(0, Math.min(atIndex, children.length))
        children.splice(at, 0, entry)
        return true
      }
      // Recurse into every container's children, whatever the target was.
      if (parsed.ttype === 'for' && isPlainObject(parsed.spec) && visit(parsed.spec.do)) return true
      if (parsed.ttype === 'fork' && isPlainObject(parsed.spec) && visit(parsed.spec.branches)) return true
      if (parsed.ttype === 'switch' && Array.isArray(parsed.spec)) {
        for (const clause of parsed.spec) {
          if (!isPlainObject(clause)) continue
          if (visit(clause.then) || visit(clause.default)) return true
        }
      }
    }
    return false
  }

  visit(clone.do)
  return clone
}

/**
 * Every task in the tree, in document order, with what the UI needs to place it.
 *
 * This exists because structural editing used to stop at the top level:
 * `removeTopLevelTask` and `moveTopLevelTask` are named honestly, and a task
 * inside a `for` body or a `switch` clause could be neither removed nor
 * reordered. That inconsistency is what made "Field mode" both necessary and
 * insufficient — it was the only place add/remove/reorder lived, and it only
 * ever showed the outermost list.
 *
 * `index`/`siblingCount` are relative to the task's OWN list, which is what
 * "orderable within its sub-hierarchy" means: a node moves among its siblings,
 * never across a container boundary. Moving between levels is a different
 * gesture (remove + add) and pretending one arrow does both would silently
 * reparent work.
 */
export interface TaskTreeEntry {
  name: string
  ttype: string
  spec: unknown
  depth: number
  parentId?: string
  /** Position within its own sibling list, and how many siblings there are. */
  index: number
  siblingCount: number
  /** For a child of a `switch`: which clause holds it. */
  clauseIndex?: number
  /** A human label for the branch a nested task sits in ("case 0", "default",
   *  "branch 1"), so the tree can say WHERE a node lives rather than only how
   *  deep it is. */
  branchLabel?: string
}

/** Walk every task list in the tree, calling `visit` with each list and its
 *  owning context. The single place the shape of a container's children is
 *  known, so the three structural operations cannot drift apart. */
function walkTaskLists(
  activity: Record<string, unknown>,
  visit: (list: unknown[], ctx: { depth: number; parentId?: string; clauseIndex?: number; branchLabel?: string }) => void,
): void {
  const descend = (list: unknown, ctx: { depth: number; parentId?: string; clauseIndex?: number; branchLabel?: string }) => {
    if (!Array.isArray(list)) return
    visit(list, ctx)
    for (const item of list) {
      const parsed = parseEntry(item)
      if (!parsed) continue
      if (parsed.ttype === 'for' && isPlainObject(parsed.spec)) {
        descend(parsed.spec.do, { depth: ctx.depth + 1, parentId: parsed.name })
      } else if (parsed.ttype === 'fork' && isPlainObject(parsed.spec)) {
        // A fork's `branches` is a list of single TASKS, not of task lists —
        // validate-activities.py walks each with walk_entry, and the graph
        // builder parses each with parseEntry. Treating it as a list of lists
        // silently skipped every forked task, which is precisely the
        // inconsistent coverage this change exists to remove.
        if (Array.isArray(parsed.spec.branches)) {
          descend(parsed.spec.branches, { depth: ctx.depth + 1, parentId: parsed.name, branchLabel: 'branch' })
        }
      } else if (parsed.ttype === 'switch' && Array.isArray(parsed.spec)) {
        parsed.spec.forEach((clause, i) => {
          if (!isPlainObject(clause)) return
          const label = 'case' in clause ? `case ${i + 1}` : 'default'
          descend(clause.then, { depth: ctx.depth + 1, parentId: parsed.name, clauseIndex: i, branchLabel: label })
          descend(clause.default, { depth: ctx.depth + 1, parentId: parsed.name, clauseIndex: i, branchLabel: label })
        })
      }
    }
  }
  descend(activity.do, { depth: 0 })
}

/** The whole task tree, in document order. */
export function getTaskTree(activity: Record<string, unknown>): TaskTreeEntry[] {
  const out: TaskTreeEntry[] = []
  walkTaskLists(activity, (list, ctx) => {
    const parsedSiblings = list.map(parseEntry).filter((p): p is ParsedEntry => p !== null)
    parsedSiblings.forEach((parsed, i) => {
      out.push({
        name: parsed.name,
        ttype: parsed.ttype,
        spec: parsed.spec,
        depth: ctx.depth,
        parentId: ctx.parentId,
        index: i,
        siblingCount: parsedSiblings.length,
        clauseIndex: ctx.clauseIndex,
        branchLabel: ctx.branchLabel,
      })
    })
  })
  return out
}

/**
 * Remove a task by name, at ANY depth.
 *
 * A no-op when the name is not found, matching the rest of this module: the
 * caller is a UI and a stale selection must not throw.
 *
 * Removing a CONTAINER removes its body with it — that is what deleting a
 * `for` means, and the caller is responsible for having asked first.
 */
export function removeTaskAnywhere(activity: Record<string, unknown>, name: string): Record<string, unknown> {
  const clone = deepClone(activity)
  walkTaskLists(clone, list => {
    for (let i = list.length - 1; i >= 0; i--) {
      if (parseEntry(list[i])?.name === name) list.splice(i, 1)
    }
  })
  return clone
}

/**
 * Move a task one place earlier (`-1`) or later (`+1`) among its OWN siblings,
 * at any depth. A no-op at either end of its list, and a no-op across a
 * container boundary — see TaskTreeEntry on why that is deliberate.
 */
export function moveTaskAnywhere(
  activity: Record<string, unknown>,
  name: string,
  delta: -1 | 1,
): Record<string, unknown> {
  const clone = deepClone(activity)
  let done = false
  walkTaskLists(clone, list => {
    if (done) return
    const from = list.findIndex(item => parseEntry(item)?.name === name)
    if (from === -1) return
    const to = from + delta
    if (to < 0 || to >= list.length) {
      // At the end of its list. Deliberately NOT hopping into the neighbouring
      // container: that would reparent the task, which is a different edit.
      done = true
      return
    }
    const [item] = list.splice(from, 1)
    list.splice(to, 0, item)
    done = true
  })
  return clone
}

/**
 * The tasks that read `name`'s output, at any depth.
 *
 * Deleting a task that others reference leaves a workflow that LOOKS whole and
 * is broken — `validate-activities.py` catches it, but only after the operator
 * has already lost the node. Naming the referrers before the delete is the
 * difference between a warning and a post-mortem.
 *
 * Matches both spellings the DSL uses: `{{steps.X.output}}` in templated
 * strings, and the bare `.steps.X.output` selector allowed in
 * `switch.case` / `for.in` / `set.<name>`.
 */
export function referrersOf(activity: Record<string, unknown>, name: string): string[] {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`\\.steps\\.${escaped}\\.output\\b`)
  return getTaskTree(activity)
    .filter(t => t.name !== name && re.test(JSON.stringify(t.spec ?? null)))
    .map(t => t.name)
}

/** The tasks that would go WITH `name` if it were removed — its body, at every
 *  depth. Empty for a leaf. Deleting a `for` means deleting its loop body, and
 *  the operator has to be told how much that is. */
export function descendantsOf(activity: Record<string, unknown>, name: string): string[] {
  const tree = getTaskTree(activity)
  const out: string[] = []
  const collect = (parent: string) => {
    for (const t of tree) {
      if (t.parentId !== parent) continue
      out.push(t.name)
      collect(t.name)
    }
  }
  collect(name)
  return out
}

/** Insert a new top-level task `{name: {ttype: spec}}` at `atIndex` (appends
 * if omitted). Returns a new activity object. */
export function addTopLevelTask(
  activity: Record<string, unknown>,
  name: string,
  ttype: TaskType,
  spec: unknown,
  atIndex?: number,
): Record<string, unknown> {
  const clone = deepClone(activity)
  const doList: unknown[] = Array.isArray(clone.do) ? (clone.do as unknown[]) : []
  const entry = { [name]: { [ttype]: spec } }
  const insertAt = atIndex === undefined ? doList.length : Math.max(0, Math.min(atIndex, doList.length))
  doList.splice(insertAt, 0, entry)
  clone.do = doList
  return clone
}

/** Remove a top-level task by name. Returns a new activity object; a no-op
 * (unchanged do list) if no top-level task has that name (it may be nested,
 * which Field mode does not support removing — see the module doc). */
export function removeTopLevelTask(activity: Record<string, unknown>, name: string): Record<string, unknown> {
  const clone = deepClone(activity)
  const doList: unknown[] = Array.isArray(clone.do) ? (clone.do as unknown[]) : []
  clone.do = doList.filter(item => parseEntry(item)?.name !== name)
  return clone
}

/** Move a top-level task from one index to another (drag-to-reorder in the
 * palette). Out-of-range indexes are clamped; a no-op if `fromIndex` is
 * out of range. Returns a new activity object. */
export function moveTopLevelTask(
  activity: Record<string, unknown>,
  fromIndex: number,
  toIndex: number,
): Record<string, unknown> {
  const clone = deepClone(activity)
  const doList: unknown[] = Array.isArray(clone.do) ? (clone.do as unknown[]) : []
  if (fromIndex < 0 || fromIndex >= doList.length) return clone
  const [item] = doList.splice(fromIndex, 1)
  const insertAt = Math.max(0, Math.min(toIndex, doList.length))
  doList.splice(insertAt, 0, item)
  clone.do = doList
  return clone
}

// ---------------------------------------------------------------------------
// ADR-079 Phase 5d — Chat mode's pure logic: merging a copilot turn into the
// shared draft + the running conversation (D1: "every mode reads and writes
// this one draft"), and scaffolding a brand-new draft from a plain agent +
// name (D7: "scaffold-from-scratch"). Kept here — not in Workflows.tsx — for
// the same reason as the rest of this module: pure/DOM-free and
// unit-testable without mounting the editor.
// ---------------------------------------------------------------------------

/** One turn of the Chat mode conversation, as rendered in the UI. Assistant
 * turns carry the copilot's own report on its output (validation/model/
 * attempts) so the panel can render them without a second round trip. */
export interface CopilotTurn {
  role: 'user' | 'assistant'
  content: string
  /** The server-assigned id of the job that produced this assistant turn. */
  jobId?: string
  /** The activity verdict for the draft this turn produced. Structurally a
   *  `ValidateActivityResult` and kept inline for the same reason the rest of this
   *  type is: `pending` is the validator's third channel and is rendered beside
   *  the chip, so a turn that dropped it would report a draft waiting on
   *  uncommitted data as plainly valid. */
  validation?: { ok: boolean; errors: string[]; pending?: string[]; note?: string }
  model?: string
  attempts?: number
  /** True when this assistant turn is an ANSWER (ask mode, or an edit turn the
   *  engine answered in prose) rather than a report on a new draft. The panel
   *  renders it without a validation chip — there is no draft to validate. */
  answer?: boolean
  /** Which engine produced the turn ("fleet-architect" / "mission-control"). */
  engine?: string
  /** ADR-134 D4 — set when the repair loop spent its whole budget with the draft
   *  still invalid. The panel renders WHY it stopped and whether the fix is the
   *  operator's to make here.
   *
   *  Before this was surfaced, the backend classified the blocker and the UI
   *  showed none of it: the operator got a rejected draft and a wall of
   *  validator text, which is the pre-D4 experience with the answer sitting one
   *  layer below, unused. */
  blocker?: CopilotBlockerInfo
  /** ADR-142 — a capability gap this turn filed. The request needed a capability
   *  the platform does not have, so the reply carries the workaround AND a
   *  durable case that waits for the capability to become usable.
   *
   *  Surfaced because it was invisible: the loop filed real cases in production
   *  while the panel showed only the rationale line, so an operator watching this
   *  page concluded nothing had happened. A gap that is filed and unseen is, to
   *  the person who asked, identical to one that was never filed. */
  capabilityGap?: CopilotCapabilityGapInfo
}

/** Convert stored API conversation messages into CopilotTurn values. */
export function conversationTurns(messages: { role: string; content: string; job_id?: string }[]): CopilotTurn[] {
  return messages.map(m => ({
    role: m.role as CopilotTurn['role'],
    content: m.content,
    ...(m.job_id ? { jobId: m.job_id } : {}),
  }))
}

/** Restore a session transcript with canonical catalog turns and preserve local trailing turns. */
export function restoreConversationTurns(
  session: { role: string; content: string }[],
  catalog: { role: string; content: string; job_id?: string }[],
): CopilotTurn[] {
  const canonical = conversationTurns(catalog)
  const restored: CopilotTurn[] = []
  let cursor = 0
  for (const turn of canonical) {
    const local = session[cursor]
    const matches = local && local.role === turn.role &&
      (turn.role !== 'user' || local.content.trim() === turn.content.trim())
    if (matches) cursor++
    restored.push(turn)
  }
  return [...restored, ...conversationTurns(session.slice(cursor))]
}

/** The catalog save payload for a live transcript, including known assistant job ids. */
export function catalogMessages(turns: CopilotTurn[]): { role: string; content: string; job_id?: string }[] {
  return turns.map(t => ({
    role: t.role,
    content: t.content,
    ...(t.role === 'assistant' && t.jobId ? { job_id: t.jobId } : {}),
  }))
}

/** Keep the live transcript's trailing user turn singular across a restore.
 * The restored turn may be a fresh object, so compare its content rather than
 * relying on object identity. */
export function ensureTrailingUserTurn(messages: CopilotTurn[], userTurn: CopilotTurn): CopilotTurn[] {
  const last = messages[messages.length - 1]
  if (last?.role === userTurn.role && last.content === userTurn.content) {
    return messages
  }
  return [...messages, userTurn]
}

/** Keep fetched answer content while restoring structured completion fields that
 * the catalog transcript cannot carry, and return applied unchanged when the live
 * transcript does not already hold this job's answer.
 *
 * A History read during a running turn can load the adopted row, which already
 * carries this turn's answer. The held turn is identified by job, never by text:
 * a repeated question answered by a different job still appends. */
export function mergeHeldAnswer(
  live: CopilotTurn[],
  applied: CopilotTurn[],
  jobId: string | undefined,
): CopilotTurn[] {
  if (!jobId) return applied
  const heldIndex = live.findIndex(m => m.role === 'assistant' && m.jobId === jobId)
  const appliedAssistant = [...applied].reverse().find(m => m.role === 'assistant')
  if (heldIndex < 0 || !appliedAssistant) return applied
  return live.map((turn, index) => index === heldIndex
    ? { ...appliedAssistant, content: turn.content, jobId }
    : turn)
}

/** ADR-142 — what the editor shows about a filed capability gap. */
export interface CopilotCapabilityGapInfo {
  want: string
  filed: boolean
  investigation_id?: string
  conversation_id?: string
  /** Set INSTEAD of the ids when a block was emitted but could not be filed — a
   *  malformed envelope, or no chat data plane. Never silently dropped. */
  note?: string
}

/** ADR-134 D4 — why the validate-repair loop could not converge. */
export interface CopilotBlockerInfo {
  errors: string[]
  /** The files those errors implicate — an operator editing one workflow needs
   *  telling that the blocker lives in three others. */
  files: string[]
  /** True when the fix lies beyond this change: a reference in a file this
   *  proposal does not touch, a missing credential, a disabled MCP, a platform
   *  defect. Inside-the-changeset blockers are the operator's to fix in the
   *  editor; outside ones are not, and the difference is what tells them whether
   *  to keep trying or to stop and hand it on. */
  outside: boolean
  attempts: number
}

/** The subset of POST /api/activities/copilot's response this module needs
 * (api/client.ts's CopilotResponse is the full, network-facing type — its
 * shape is duplicated structurally here rather than imported, so this
 * module stays free of the API client / auth stack, same as the rest of
 * workflowDraft.ts). */
export interface CopilotAPIResult {
  draft: string
  rationale: string
  validation: { ok: boolean; errors: string[]; note?: string }
  model: string
  attempts: number
  job_id?: string
  answer?: string
  draft_changed?: boolean
  engine?: string
  /** ADR-134 D1 — extra files the turn proposes alongside the draft (the cron
   *  that drives the workflow, a skill that routes to it, a second activity it
   *  fans out into). Absent on a single-file edit and on every answer. */
  files?: { path: string; content?: string; op?: 'write' | 'delete' }[]
  blocker?: CopilotBlockerInfo
  capability_gap?: CopilotCapabilityGapInfo
}

export interface AppliedCopilotTurn {
  messages: CopilotTurn[]
  /** ADR-134 D1 — extra files to adopt into the changeset. Only ever set
   *  alongside an adopted draft: a failed turn's partial changeset would land in
   *  the operator's editor as files they did not ask for and cannot propose. */
  extraFiles?: { path: string; content?: string; op?: 'write' | 'delete' }[]
  draftYAML: string
  draftActivity: Record<string, unknown> | null
  draftParseError: string | null
}

/**
 * Append the copilot's response as an assistant turn onto `messages` (which
 * should already include the just-sent user turn) and — ONLY when the response
 * carries a new draft — load it as the new shared draft, the same one Field/YAML
 * mode and the graph preview read (ADR-079 D1).
 *
 * That conditional is the fix for a data-loss bug. This function used to return
 * `result.draft` unconditionally, and the caller assigned it unconditionally. A
 * response with no YAML — which is what asking a QUESTION produced, since the
 * copilot had no question mode — therefore replaced the operator's in-progress
 * draft with an empty string. Asking the copilot to review a draft deleted it.
 *
 * `draft_changed` is the server's explicit statement that `draft` is adoptable;
 * the non-empty check is a second, independent guard, because the harm here is
 * silent and unrecoverable (the editor holds the only copy of an unsaved draft).
 *
 * An invalid draft (`result.validation.ok === false`) is still loaded — D4's
 * deterministic gate is enforced at Propose, not here, so the human can see
 * and fix the flagged issues in any mode; the caller is expected to keep the
 * invalid state visible (Propose already disables itself on an invalid
 * draft).
 */
export function applyCopilotResponse(
  messages: CopilotTurn[],
  result: CopilotAPIResult,
  currentDraftYAML: string,
): AppliedCopilotTurn {
  const answer = (result.answer ?? '').trim()
  // Two independent conditions, and their asymmetry is deliberate:
  //
  //   - A non-empty draft is REQUIRED. This alone closes the wipe, because the
  //     harmful response was `draft: ""` — an empty draft is now never adopted no
  //     matter what else the response says.
  //   - `draft_changed` may only VETO. Requiring it to be exactly `true` would
  //     mean a server that predates the field silently stops applying drafts at
  //     all — trading a loud bug for a quiet one, which is the pattern this whole
  //     change exists to stop.
  const adoptDraft = result.draft.trim() !== '' && result.draft_changed !== false

  if (answer !== '' && !adoptDraft) {
    // An answer, not an edit: render the prose and leave the draft exactly as
    // the operator left it.
    const parsed = parseDraftYAML(currentDraftYAML)
    return {
      messages: [...messages, {
        role: 'assistant',
        content: answer,
        jobId: result.job_id,
        model: result.model,
        attempts: result.attempts,
        answer: true,
        engine: result.engine,
      }],
      draftYAML: currentDraftYAML,
      draftActivity: parsed.activity,
      draftParseError: parsed.error,
    }
  }

  const assistantTurn: CopilotTurn = {
    role: 'assistant',
    content: result.rationale.trim() || answer || '(no rationale returned)',
    jobId: result.job_id,
    validation: result.validation,
    model: result.model,
    attempts: result.attempts,
    engine: result.engine,
    // ADR-134 D4: carry the blocker onto the turn so the panel can say WHY the
    // loop stopped. It only ever accompanies a failed turn, but it is attached
    // unconditionally — a server that sends one alongside a successful draft is
    // telling us something, and dropping it here would hide that.
    blocker: result.blocker,
    // ADR-142: same reasoning as the blocker above — attached unconditionally,
    // including alongside a SUCCESSFUL draft, because a turn that authored a
    // working workflow and reported a better shape is the normal case here.
    capabilityGap: result.capability_gap,
  }
  if (!adoptDraft) {
    // No adoptable draft (a failed turn). Keep what the operator had; the
    // validation errors on the turn say what went wrong.
    const parsed = parseDraftYAML(currentDraftYAML)
    return {
      messages: [...messages, assistantTurn],
      draftYAML: currentDraftYAML,
      draftActivity: parsed.activity,
      draftParseError: parsed.error,
    }
  }
  const parsed = parseDraftYAML(result.draft)
  return {
    messages: [...messages, assistantTurn],
    extraFiles: result.files,
    draftYAML: result.draft,
    draftActivity: parsed.activity,
    draftParseError: parsed.error,
  }
}

/**
 * A blank scaffold draft for the "New workflow" affordance (ADR-079 D7 —
 * scaffold-from-scratch): a minimal, schema-shaped dsl activity with an
 * empty `do` list, ready to hand straight to the copilot ("build me a
 * workflow that…") or to Field/YAML mode directly.
 */
export function buildScaffoldYAML(agent: string, name: string): string {
  const activity = {
    dsl: DSL_MARKER,
    name,
    description: '',
    version: '1.0.0',
    owner: agent,
    do: [],
  }
  return serializeDraftYAML(activity)
}

// ---------------------------------------------------------------------------
// Legacy single-prompt -> DSL migration seed (owner decision, 2026-07:
// "seed it in the Workflows-facet chat, no dedicated heavy feature" — the
// migration path for legacy single-prompt activities is a Chat-mode seed
// message, reusing the SAME copilot/draft-merge/propose plumbing as any
// other Chat turn; see Workflows.tsx's "Convert to DSL flow" affordance).
//
// CONVERT_LEGACY_TO_DSL_INSTRUCTIONS is exported as a standalone, named
// constant (rather than inlined where it's used) so this exact instruction
// text can be reused headlessly later — e.g. a batch/CLI migration sweep —
// without re-deriving or duplicating the prose.
// ---------------------------------------------------------------------------

/**
 * The canonical instruction body the copilot receives to convert a legacy
 * single-prompt (stages/steps) activity into an equivalent deterministic
 * DSL (`do:` graph) activity. {@link buildConvertToDslSeed} appends the
 * legacy activity's own YAML as a fenced block after this text.
 */
export const CONVERT_LEGACY_TO_DSL_INSTRUCTIONS = `Convert the legacy single-prompt activity below into an equivalent deterministic DSL workflow (a valid \`do:\` graph, per the ADR-075 schema).

This is a faithful migration, not a redesign — the new DSL workflow MUST preserve every behavior of the legacy prompt:
- Every scope gate (what the activity is/isn't allowed to touch or act on).
- Each numbered or implied step, in the same order, with no step dropped, merged away, or silently added.
- All section moves, custom-field updates, and other object operations, including their exact GIDs.
- Every skip/guard condition (when a step is conditionally skipped or short-circuits).
- Every ordering constraint stated or implied by the prompt (e.g. "always comment before moving to the next section" must still comment before the section move in the DSL graph).
- The final delivery step (e.g. posting a summary to Slack), wired to the same destination.

Keep every project GID, section GID, custom-field GID, and channel ID exactly as written in the legacy activity below — verbatim. Never invent a GID/ID that isn't already present, and never drop one.

Return ONLY the workflow draft, per your normal contract (no extra commentary outside the rationale).

The legacy activity to convert:`

/**
 * Build the full Chat-mode seed message for the "Convert to DSL flow"
 * affordance: the canonical conversion instructions followed by the legacy
 * activity's raw YAML in a fenced block, exactly as the copilot expects a
 * pasted reference document. `legacyYAML` MUST be the CURRENT DRAFT
 * (Workflows.tsx's draftYAML) — the same document handleChatSend passes as
 * copilotAuthor's `currentDraft` — and NOT the on-disk detail.yaml, which
 * can be stale the moment the operator edits in YAML mode (detail is never
 * updated after the editor session opens). Seeding from a stale on-disk
 * copy while the live draft is passed as "current draft to edit" would hand
 * the copilot two contradictory documents and silently drop the operator's
 * edits (e.g. a GID fix).
 */
export function buildConvertToDslSeed(legacyYAML: string): string {
  return `${CONVERT_LEGACY_TO_DSL_INSTRUCTIONS}\n\n\`\`\`yaml\n${legacyYAML.trim()}\n\`\`\``
}

/** The outcome of ensuring a `data:` pin exists for an inserted reference. */
export interface EnsurePinResult {
  activity: Record<string, unknown>
  /** Set when the pin could NOT be established, and why. The caller must not insert. */
  refusal?: string
}

/**
 * Ensure the activity pins `alias` to `setRef`, so an inserted reference validates.
 *
 * WHY THE PICKER HAS TO DO THIS. A `{{data.<alias>.…}}` reference with no pin is a
 * VALIDATION ERROR, not a warning — "every consumer declares its pin in a `data:`
 * block (ADR-125 D6). An undeclared reference is a validation error." So until now
 * every token the picker inserted was invalid on arrival unless the artifact
 * already happened to pin that alias, and the operator had to hand-write the pin
 * afterwards. ADR-127 D15 says the opposite: a reference the picker produces is the
 * PICKER's responsibility, not the operator's.
 *
 * Three cases, and the third is the one worth being careful about:
 *
 *   absent            -> add the pin
 *   present, same set -> nothing to do
 *   present, OTHER set -> REFUSE, and say so
 *
 * Rebinding an alias already pointing at a different set would silently repoint
 * EVERY existing reference through it — a one-line edit changing values all over
 * the workflow. Renaming the alias instead would mean rewriting the token the
 * picker already rendered, i.e. re-deriving the token grammar in TypeScript, which
 * is the drift ADR-128 D3 forbids. So this refuses and hands the operator the one
 * fact they need to resolve it.
 */
export function ensureDataPin(
  activity: Record<string, unknown> | null,
  alias: string,
  setRef: string,
): EnsurePinResult {
  const clone = deepClone(activity ?? {}) as Record<string, unknown>
  const block = isPlainObject(clone.data) ? clone.data : {}
  const existing = block[alias]

  if (typeof existing === 'string' && existing !== setRef) {
    return {
      activity: clone,
      refusal:
        `This workflow already pins "${alias}" to ${existing}. Inserting a reference to `
        + `${setRef} through the same alias would repoint every existing `
        + `{{data.${alias}.…}} in this workflow at a different set. Rename the existing pin `
        + `(or the new one) first.`,
    }
  }
  // A map-form pin ({set, freshness, args}) is ADR-126 D2's other spelling; leaving
  // it alone is deliberate — it carries settings this insertion knows nothing about,
  // and overwriting them to add a reference would be a silent downgrade.
  if (isPlainObject(existing)) {
    const pinned = (existing as Record<string, unknown>).set
    if (typeof pinned === 'string' && pinned !== setRef) {
      return {
        activity: clone,
        refusal:
          `This workflow already pins "${alias}" to ${pinned} (with its own freshness/args). `
          + `Inserting a reference to ${setRef} through that alias would change what every `
          + `existing reference resolves to. Rename one of them first.`,
      }
    }
    return { activity: clone }
  }
  if (existing === setRef) return { activity: clone }

  clone.data = { ...block, [alias]: setRef }
  return { activity: clone }
}

// ---------------------------------------------------------------------------
// DATA REFERENCE SELECTIONS — one mutation for the fields AND their pins
//
// A picked reference is two edits that must land together: the token in the field,
// and the `data:` pin that makes the token resolve. Applied as two draft mutations
// they race — the second reads the state the first replaced, and whichever runs last
// wins with the other half missing. So the whole acceptance, however many rows the
// operator ticked, is one function over one clone.
// ---------------------------------------------------------------------------

/** One literal, at its location, swapped for a reference whose resolved value equals it.
 *  `literal` is what the matcher saw; the apply refuses if the draft no longer holds it. */
export interface DataReferenceReplaceSelection {
  kind: 'replace'
  location: DataReferenceMatchLocation
  literal: string | number | boolean
  candidate: DataReferenceMatchCandidate
}

/** A token dropped at the caret. The field may already hold prose and other references, so
 *  nothing about its value is checked — only that `range` still addresses the value the
 *  operator was looking at. */
export interface DataReferenceInsertSelection {
  kind: 'insert'
  location: DataReferenceMatchLocation
  token: string
  pin: { alias: string; set: string }
  range: { start: number; end: number }
}

/** A whole field replaced by a value the HOST computed from the field's display text.
 *
 *  `command.args` is one textarea holding the comma-joined array, so a caret offset in it
 *  addresses no single array index — but the pin and the field still have to land in one
 *  mutation, which is the defect ADR-179 design decision 10 exists to close. The host does
 *  the display-to-value translation it already owns (the comma re-split) and hands the
 *  finished value here; this module writes it at the slot and pins alongside. The location
 *  carries no `index`/`key`: the whole slot is the target. */
export interface DataReferenceWriteFieldSelection {
  kind: 'write-field'
  location: DataReferenceMatchLocation
  value: unknown
  pin: { alias: string; set: string }
}

export type DataReferenceSelection =
  | DataReferenceReplaceSelection
  | DataReferenceInsertSelection
  | DataReferenceWriteFieldSelection

/** The outcome of applying a set of selections. `refusal` means NOTHING was written —
 *  a half-applied sweep is worse than a refused one, because the operator cannot see
 *  which rows landed. */
export interface ApplyDataReferenceSelectionsResult {
  activity: Record<string, unknown>
  refusal?: string
}

/** The separator the args field shows between entries. `splitCommandArgs` removes exactly
 *  this and nothing else, so an entry's own whitespace survives a blur. */
const COMMAND_ARGS_SEPARATOR = ', '

/** The `command.args` field is one text input over the whole array, entries joined with a
 *  comma and a space. This is the display side of that pairing. */
export function joinCommandArgs(args: string[]): string {
  return args.join(COMMAND_ARGS_SEPARATOR)
}

/** The blur side: the display text back into entries. Whitespace inside an entry is data —
 *  spartan's drive-upload prefix is `'CHECK - '`, trailing space included — so only the one
 *  separator space the join inserted is removed. Entries typed without that space still read
 *  the same; an empty entry (a trailing or doubled comma) is dropped. */
export function splitCommandArgs(text: string): string[] {
  return text
    .split(',')
    .map((part, i) => (i > 0 && part.startsWith(' ') ? part.slice(1) : part))
    .filter(part => part.length > 0)
}

/**
 * A location in the words a message about it has to use — the operator picked the row by
 * task and slot, so that is what names it back to them.
 *
 * One definition for all three readers (this module's refusals, the page's, and the sweep
 * panel's row labels), because two spellings of one address drift the day the tuple gains a
 * field. `sep` is what goes between the task and the slot, and `quote` wraps the task name.
 */
export function describeLocation(
  loc: DataReferenceMatchLocation, sep = ' ', quote = '',
): string {
  const tail = typeof loc.index === 'number' ? `[${loc.index}]`
    : typeof loc.key === 'string' ? `.${loc.key}`
      : ''
  return `${quote}${loc.task}${quote}${sep}${loc.slot}${tail}`
}

/** The same address as a string, for use as a React key, a test id or a Set identity. Built
 *  from the address itself, so two locations collide only if they are the same site. Operator-
 *  facing text is `describeLocation`'s job — this one is never shown. */
export function locationKey(loc: DataReferenceMatchLocation): string {
  const suffix = typeof loc.index === 'number' ? `index:${loc.index}`
    : typeof loc.key === 'string' ? `key:${loc.key}` : ''
  return `${loc.task}|${loc.slot}|${suffix}`
}

/** How this module names a location inside a refusal. */
function where(loc: DataReferenceMatchLocation): string {
  return describeLocation(loc, ' ', '`')
}

/** The mapping a task holds at one of the three mapping sites, or null when it holds none.
 *  A `set` task's spec IS its mapping (same rule `getTaskMapping`/`updateTaskMapping`
 *  follow); `python.inputs` and `run.inputs` both live under `inputs`. */
function mappingAt(raw: unknown, slot: string): Record<string, unknown> | null {
  if (!isPlainObject(raw)) return null
  if (slot === 'set') return raw
  const inputs = raw.inputs
  return isPlainObject(inputs) ? inputs : null
}

/** The value the draft currently holds at a location, or `undefined` when the location
 *  no longer addresses anything. Only the template slots the matcher scans are addressable. */
function valueAtLocation(
  activity: Record<string, unknown>,
  loc: DataReferenceMatchLocation,
): unknown {
  const found = findTaskRaw(activity.do, loc.task)
  if (!found) return undefined
  if (loc.slot === 'agent.run') {
    return isPlainObject(found.raw) ? found.raw.run : undefined
  }
  if (loc.slot === 'command.args') {
    const args = isPlainObject(found.raw) ? found.raw.args : undefined
    if (!Array.isArray(args) || typeof loc.index !== 'number') return undefined
    return args[loc.index]
  }
  if (loc.slot === 'python.inputs' || loc.slot === 'run.inputs' || loc.slot === 'set') {
    const mapping = mappingAt(found.raw, loc.slot)
    if (!mapping || typeof loc.key !== 'string') return undefined
    return mapping[loc.key]
  }
  return undefined
}

/** Write a value at a location IN PLACE. Returns false when the location is not
 *  addressable, which the caller has already ruled out by reading it first. */
function writeAtLocation(
  activity: Record<string, unknown>,
  loc: DataReferenceMatchLocation,
  value: unknown,
): boolean {
  const found = findTaskRaw(activity.do, loc.task)
  if (!found || !isPlainObject(found.raw)) return false
  if (loc.slot === 'agent.run') {
    found.raw.run = value
    return true
  }
  if (loc.slot === 'command.args') {
    // No index means the whole array is the target — a `write-field` selection, where the
    // host re-split the comma-joined display text it owns.
    if (typeof loc.index !== 'number') {
      if (!Array.isArray(value)) return false
      found.raw.args = value
      return true
    }
    const args = found.raw.args
    if (!Array.isArray(args)) return false
    args[loc.index] = value
    return true
  }
  const mapping = mappingAt(found.raw, loc.slot)
  if (!mapping || typeof loc.key !== 'string' || !(loc.key in mapping)) return false
  mapping[loc.key] = value
  return true
}

/**
 * Apply a set of picked data references to the draft — the field writes and the `data:`
 * pins they need, in one mutation over one clone.
 *
 * All or nothing. A `replace` whose literal has moved on, a `range` that no longer fits its
 * field, or a pin {@link ensureDataPin} refuses, refuses the WHOLE apply and returns the
 * input unchanged. The alternative is a draft holding some of the rows the operator ticked
 * with nothing saying which, and a pin for a token that was never written.
 *
 * Serialization stays the caller's job: this returns the activity object, and the page hands
 * it to `applyDraftMutation` like every other mutator here.
 */
export function applyDataReferenceSelections(
  activity: Record<string, unknown>,
  selections: DataReferenceSelection[],
): ApplyDataReferenceSelectionsResult {
  const unchanged = (refusal: string): ApplyDataReferenceSelectionsResult =>
    ({ activity: deepClone(activity), refusal })

  // Every write is decided against the draft as it stands BEFORE anything is written, so
  // one refused row cannot be masked by an earlier row's edit. That also means two
  // selections for ONE location would both splice into the original value and the second
  // would silently win, so the same location twice is refused outright.
  const writes: { location: DataReferenceMatchLocation; value: unknown }[] = []
  const targeted = new Set<string>()
  for (const sel of selections) {
    const at = where(sel.location)
    const identity = locationKey(sel.location)
    if (targeted.has(identity)) {
      return unchanged(
        `${at} was picked twice in one apply. Each write is computed against the draft as `
        + `it stands, so the second would overwrite the first — nothing was applied.`)
    }
    targeted.add(identity)

    if (sel.kind === 'write-field') {
      writes.push({ location: sel.location, value: sel.value })
      continue
    }

    const current = valueAtLocation(activity, sel.location)
    if (sel.kind === 'replace') {
      if (current === undefined) {
        return unchanged(`${at} is no longer in this workflow. Review the matches again.`)
      }
      if (current !== sel.literal) {
        return unchanged(
          `${at} now holds ${JSON.stringify(current)}, not `
          + `${JSON.stringify(sel.literal)}. The draft changed since it was matched, so `
          + `nothing was applied — review the matches again.`)
      }
      writes.push({ location: sel.location, value: sel.candidate.token })
      continue
    }

    if (current === undefined) {
      return unchanged(`${at} is no longer in this workflow. Review the matches again.`)
    }
    // A mapping row may legally hold a number, a boolean, a list or an object, and the
    // editor shows it as its JSON text (getTaskMapping). The caret offset was taken against
    // THAT text, so the insertion is spliced into it and the row is written back as a
    // string — which is exactly what the pre-ADR-179 `updateTaskMapping` path did. Refusing
    // here instead would have been a silent regression on a path that already worked.
    const text = typeof current === 'string' ? current : JSON.stringify(current)
    const { start, end } = sel.range
    if (!(start >= 0 && end >= start && end <= text.length)) {
      return unchanged(
        `${at} has changed since the insertion point was chosen, so the reference would `
        + `land somewhere else. Nothing was applied — place the caret again.`)
    }
    // The same padding rule the caret affordance has always used: a token butted against a
    // word reads as part of it, and `agent.run` is prose being handed to a model.
    writes.push({ location: sel.location, value: insertAtCaret(text, sel.token, start, end).value })
  }

  // Pins first: ensureDataPin returns a fresh clone each time, so it owns the object the
  // field writes then go into. One pin per alias+set, however many rows chose that set.
  let working = deepClone(activity)
  const pinned = new Set<string>()
  for (const sel of selections) {
    const pin = sel.kind === 'replace' ? sel.candidate.pin : sel.pin
    const seen = `${pin.alias}\0${pin.set}`
    if (pinned.has(seen)) continue
    pinned.add(seen)
    const result = ensureDataPin(working, pin.alias, pin.set)
    if (result.refusal) return unchanged(result.refusal)
    working = result.activity
  }

  for (const write of writes) {
    if (!writeAtLocation(working, write.location, write.value)) {
      return unchanged(
        `${where(write.location)} could not be written. Nothing was applied.`)
    }
  }
  return { activity: working }
}
